/**
 * SessionBroadcastService.ts
 *
 * Enterprise-grade service for cross-tab session synchronization.
 * Encapsulates BroadcastChannel logic and security validations to ensure 
 * strict session isolation and anti-replay protection.
 */
import { SESSION_BROADCAST_CHANNEL_NAME } from '../constants/securityConstants';
import { SessionEvent } from '../types/security.types';
import configurationService from './ConfigurationService';
import logger from './loggerService';

export class SessionBroadcastService {
  private static instance: SessionBroadcastService;
  private channel: BroadcastChannel | null = null;
  private listeners: ((event: SessionEvent) => void)[] = [];

  private constructor() {
    this.initChannel();
  }

  /**
   * Singleton accessor for global session synchronization.
   */
  public static getInstance(): SessionBroadcastService {
    if (!SessionBroadcastService.instance) {
      SessionBroadcastService.instance = new SessionBroadcastService();
    }
    return SessionBroadcastService.instance;
  }

  /**
   * Initializes the native BroadcastChannel if supported by the environment.
   */
  private initChannel(): void {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        this.channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL_NAME);
        this.channel.onmessage = (event: MessageEvent<SessionEvent>) => {
          this.handleMessage(event.data);
        };
      } catch (error) {
        logger.error('Failed to initialize BroadcastChannel for session synchronization.', error, 'SessionBroadcastService');
      }
    } else {
        logger.warn('BroadcastChannel API not supported in this environment. Cross-tab sync disabled.', { context: 'SessionBroadcastService' });
    }
  }

  /**
   * Internal handler for incoming broadcast messages.
   * Performs forensic validation before triggering listeners.
   */
  private handleMessage(payload: SessionEvent): void {
    if (payload?.type === 'SESSION_TERMINATED') {
      // ENTERPRISE SECURITY: Validate payload structure
      if (!payload.timestamp || typeof payload.timestamp !== 'number' || !payload.userId) {
        logger.warn('Rejected malformed cross-tab session termination signal.', { 
           context: 'SessionBroadcastService',
           payload
        });
        return;
      }

      // ENTERPRISE SECURITY: Anti-Replay Validation
      const maxAge = configurationService.getSecurityConfig().maxBroadcastAgeMs;
      const ageMs = Date.now() - payload.timestamp;

      // Reject stale (stale) OR future (forged) signals
      if (ageMs > maxAge || ageMs < 0) {
        logger.warn('Rejected suspicious cross-tab session termination signal (Anti-Replay / Clock Skew).', { 
           context: 'SessionBroadcastService',
           ageMs,
           maxAllowedMs: maxAge
        });
        return;
      }

      // Notify all registered domain-level listeners
      this.listeners.forEach(listener => {
          try {
              listener(payload);
          } catch (error) {
              logger.error('Error executing session termination listener.', error, 'SessionBroadcastService');
          }
      });
    }
  }

  /**
   * Broadcasts a cryptographically bound session termination event.
   * Forces all other tabs on the same origin to synchronize their logout state.
   */
  public broadcastTermination(userId: string): void {
    if (!this.channel) {
      logger.warn('BroadcastChannel not available. Cannot synchronize termination.', { context: 'SessionBroadcastService', userId });
      return;
    }

    const payload: SessionEvent = {
      type: 'SESSION_TERMINATED',
      timestamp: Date.now(),
      userId
    };

    try {
      this.channel.postMessage(payload);
      logger.info('Broadcasted verified session termination signal.', { context: 'SessionBroadcastService', userId });
    } catch (error) {
      logger.error('Failed to post session termination message.', error, 'SessionBroadcastService');
    }
  }

  /**
   * Registers a domain-level listener for session synchronization events.
   * Returns an unsubscription function for clean teardown.
   */
  public subscribe(listener: (event: SessionEvent) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Gracefully shuts down the channel.
   * Should be used during application teardown or testing.
   */
  public close(): void {
      if (this.channel) {
          this.channel.close();
          this.channel = null;
      }
  }
}

export default SessionBroadcastService.getInstance();
